/**
 * まとめて精算の一覧PDF(2026-09-04 発注者指示)。
 * チェックした立替をまとめて精算するとき、明細(管理番号・支払日・科目・摘要・金額)と
 * 合計を1枚(以上)のA4にまとめ、受領欄(サイン)を付けてPDFにする。
 * 受領書PDFと同じく、日本語はcanvasで描いてPDFに埋め込む(フォント埋め込み不要)。
 */
import { PDFDocument } from 'pdf-lib';
import { formatYen } from '@core/lib/money';
import { toShortDate } from '@core/lib/date';

export type SettlementListRow = {
  code: string;
  paymentDate: string;
  account: string;
  description: string;
  amount: number;
};

export type SettlementListParams = {
  /** 会社名(2026-09-07: 会社ごとに精算するため見出しに出す) */
  companyName?: string;
  payeeName: string;
  settlementDate: string;
  method: 'cash' | 'bank_transfer';
  cashAccountName: string;
  issuerName: string;
  rows: SettlementListRow[];
  /** 現金のとき: 受領者の手書きサイン */
  signatureImage?: HTMLImageElement | null;
};

const W = 1240;
const H = 1754;
const ROWS_PER_PAGE = 26;
const SANS = '"Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';

function ellipsis(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(s + '…').width > maxWidth) s = s.slice(0, -1);
  return s + '…';
}

/** 1ページ分を描く。最終ページには合計と受領欄を描く */
function drawPage(p: SettlementListParams, pageRows: SettlementListRow[], pageNo: number, pageCount: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#1a1a1a';

  // 見出し
  ctx.font = `bold 46px ${SANS}`;
  ctx.textAlign = 'center';
  ctx.fillText('立替精算 一覧', W / 2, 120);
  if (p.companyName) {
    ctx.font = `bold 28px ${SANS}`;
    ctx.fillText(p.companyName, W / 2, 162);
  }
  ctx.textAlign = 'left';
  ctx.font = `26px ${SANS}`;
  ctx.fillText(`精算日: ${toShortDate(p.settlementDate)}`, 100, 200);
  ctx.fillText(`立替者: ${p.payeeName} 様`, 100, 244);
  ctx.fillText(`支払方法: ${p.method === 'cash' ? '現金' : '振込'} / ${p.cashAccountName}`, 100, 288);
  ctx.textAlign = 'right';
  ctx.fillText(`${pageNo} / ${pageCount} ページ`, W - 100, 200);
  ctx.fillText(`発行: ${p.issuerName}`, W - 100, 244);
  ctx.textAlign = 'left';

  // 表(列の位置は固定)
  const cNo = { label: 'No', x: 100, w: 60 };
  const cCode = { label: '管理番号', x: 160, w: 200 };
  const cDate = { label: '支払日', x: 360, w: 150 };
  const cAcct = { label: '科目', x: 510, w: 200 };
  const cDesc = { label: '摘要', x: 710, w: 300 };
  const cAmt = { label: '金額', x: 1010, w: 130 };
  const cols = [cNo, cCode, cDate, cAcct, cDesc, cAmt];
  const top = 340;
  const rowH = 44;
  ctx.font = `bold 22px ${SANS}`;
  ctx.fillStyle = '#EEF2F8';
  ctx.fillRect(100, top, W - 200, rowH);
  ctx.fillStyle = '#1a1a1a';
  for (const c of cols) {
    ctx.textAlign = c.label === '金額' ? 'right' : 'left';
    ctx.fillText(c.label, c.label === '金額' ? c.x + c.w : c.x + 8, top + 30);
  }
  ctx.strokeStyle = '#B8C2D6';
  ctx.lineWidth = 2;
  ctx.font = `22px ${SANS}`;
  const startIndex = (pageNo - 1) * ROWS_PER_PAGE;
  pageRows.forEach((r, i) => {
    const y = top + rowH * (i + 1);
    ctx.beginPath();
    ctx.moveTo(100, y + rowH);
    ctx.lineTo(W - 100, y + rowH);
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.fillText(String(startIndex + i + 1), cNo.x + 8, y + 30);
    ctx.fillText(ellipsis(ctx, r.code, cCode.w - 16), cCode.x + 8, y + 30);
    ctx.fillText(toShortDate(r.paymentDate), cDate.x + 8, y + 30);
    ctx.fillText(ellipsis(ctx, r.account, cAcct.w - 16), cAcct.x + 8, y + 30);
    ctx.fillText(ellipsis(ctx, r.description, cDesc.w - 16), cDesc.x + 8, y + 30);
    ctx.textAlign = 'right';
    ctx.fillText(`${formatYen(r.amount)}円`, cAmt.x + cAmt.w, y + 30);
  });
  ctx.strokeRect(100, top, W - 200, rowH * (pageRows.length + 1));

  if (pageNo === pageCount) {
    const total = p.rows.reduce((s, r) => s + r.amount, 0);
    const y = top + rowH * (pageRows.length + 1) + 70;
    ctx.textAlign = 'right';
    ctx.font = `bold 30px ${SANS}`;
    ctx.fillText(`合計 ${p.rows.length}件  ${formatYen(total)}円`, W - 100, y);

    // 受領欄
    const boxY = Math.max(y + 80, H - 520);
    ctx.textAlign = 'left';
    ctx.font = `24px ${SANS}`;
    if (p.method === 'cash') {
      ctx.fillText('上記の合計金額を立替精算金として受領しました。', 100, boxY);
      ctx.fillText(`受領日: ${toShortDate(p.settlementDate)}`, 100, boxY + 50);
      ctx.fillText('受領者署名', 100, boxY + 170);
      ctx.beginPath();
      ctx.moveTo(300, boxY + 180);
      ctx.lineTo(900, boxY + 180);
      ctx.stroke();
      if (p.signatureImage) {
        // 署名は下線の上に収める
        const maxW = 560;
        const maxH = 150;
        const ratio = Math.min(maxW / p.signatureImage.width, maxH / p.signatureImage.height);
        const sw = p.signatureImage.width * ratio;
        const sh = p.signatureImage.height * ratio;
        ctx.drawImage(p.signatureImage, 320, boxY + 175 - sh, sw, sh);
      }
      ctx.font = `20px ${SANS}`;
      ctx.fillStyle = '#5F7194';
      ctx.fillText('(画面上で本人が手書きサイン)', 920, boxY + 175);
    } else {
      ctx.fillText('振込により精算しました(振込明細を添付)。', 100, boxY);
      ctx.fillText(`振込日: ${toShortDate(p.settlementDate)}`, 100, boxY + 50);
    }
  }
  return canvas;
}

async function canvasToPng(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('画像化に失敗しました'))), 'image/png'),
  );
  return new Uint8Array(await blob.arrayBuffer());
}

/** 一覧PDFのファイルを作る(A4・複数ページ対応) */
export async function buildSettlementListPdf(p: SettlementListParams, fileName: string): Promise<File> {
  const pageCount = Math.max(1, Math.ceil(p.rows.length / ROWS_PER_PAGE));
  const doc = await PDFDocument.create();
  for (let i = 0; i < pageCount; i++) {
    const rows = p.rows.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE);
    const canvas = drawPage(p, rows, i + 1, pageCount);
    const image = await doc.embedPng(await canvasToPng(canvas));
    const page = doc.addPage([595.28, 841.89]);
    page.drawImage(image, { x: 0, y: 0, width: 595.28, height: 841.89 });
  }
  const bytes = await doc.save();
  return new File([new Uint8Array(bytes)], fileName, { type: 'application/pdf' });
}
